import { useEffect, useRef, useState } from 'react';
import { Modal, Button, Popconfirm, message } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import ConfettiExplosion from 'react-confetti-explosion';
import dompurify from 'dompurify';
import GiftImage from 'src/_shared/assets/snappy_present.png';
import {
	GIFT_STATE,
	PIN_STATE,
	formatClaimBy,
	isGiftOutstanding,
	isGiftResolved,
	isPinOutstanding,
	isPinResolved,
	needsAttention,
} from './milestoneClaims.js';
import './CombinedMilestoneClaimModal.scss';

const CONFETTI_DURATION = 3000;

const confettiConfig = {
	force: 0.9,
	duration: CONFETTI_DURATION,
	particleCount: 300,
	width: '2000',
	height: 1600,
	colors: ['#f650a9', '#00c3ff', '#fffc00', '#00ff87', '#ff0055', '#7700ff'],
};

const openLink = (link) => window.open(link, '_blank', 'noopener,noreferrer');

const ClaimedMark = () => (
	<span className="milestone-claim-check" aria-hidden="true">
		<CheckOutlined />
	</span>
);

const CombinedMilestoneClaimModal = ({
	currentUser,
	claim,
	open,
	onClose,
	onClaimPin,
	onDeclinePin,
	onClaimGift,
	onOpenGift,
}) => {
	const [isExploding, setIsExploding] = useState(false);
	const [claimingPin, setClaimingPin] = useState(false);
	const [decliningPin, setDecliningPin] = useState(false);
	const [claimingGift, setClaimingGift] = useState(false);
	const hasExploded = useRef(false);

	const recognitionSettings = currentUser?.company?.recognitionSettings || {};
	const pin = claim?.pin;
	const gift = claim?.gift;
	const pinResolved = isPinResolved(claim);
	const giftResolved = isGiftResolved(claim);
	const isFirstVisit = !pinResolved && !giftResolved;
	const resolvedCount = (pinResolved ? 1 : 0) + (giftResolved ? 1 : 0);
	const yearsLabel = claim?.years ? `${claim.years} years · ` : '';
	const giftClaimBy = formatClaimBy(gift?.claimBy);

	useEffect(() => {
		if (open && isFirstVisit && !hasExploded.current) {
			hasExploded.current = true;
			setIsExploding(true);
			const timer = setTimeout(
				() => setIsExploding(false),
				CONFETTI_DURATION + 500
			);
			return () => clearTimeout(timer);
		}
	}, [open, isFirstVisit]);

	if (!claim) return null;

	let title =
		recognitionSettings.milestoneClaimModalTitle ||
		'Celebrating Your Service Anniversary';
	if (!needsAttention(claim)) {
		title = "You're all set";
	} else if (pinResolved && isGiftOutstanding(claim)) {
		title = "Don't forget your anniversary gift";
	} else if (giftResolved && isPinOutstanding(claim)) {
		title = "Don't forget your service pin";
	}

	const handleClaimPin = async () => {
		try {
			setClaimingPin(true);
			const link = await onClaimPin(claim);
			if (link) {
				openLink(link);
			} else {
				message.success('Your pin has been claimed!');
			}
		} catch (error) {
			console.error('Error claiming milestone pin:', error);
			message.error('Something went wrong claiming your pin. Please try again.');
		} finally {
			setClaimingPin(false);
		}
	};

	const handleDeclinePin = async () => {
		try {
			setDecliningPin(true);
			await onDeclinePin(claim);
		} catch (error) {
			console.error('Error declining milestone pin:', error);
			message.error('Something went wrong. Please try again.');
		} finally {
			setDecliningPin(false);
		}
	};

	const handleClaimGift = async () => {
		// Open synchronously when the link already exists so the browser doesn't block it
		if (gift.link) {
			openLink(gift.link);
			onOpenGift(claim);
			return;
		}

		try {
			setClaimingGift(true);
			const link = await onClaimGift(claim);
			if (link) {
				openLink(link);
			} else {
				message.error('Error claiming gift. Please try again.');
			}
		} catch (error) {
			console.error('Error claiming milestone gift:', error);
			message.error('Something went wrong claiming your gift. Please try again.');
		} finally {
			setClaimingGift(false);
		}
	};

	const renderPinCard = () => {
		let status = 'Reward 1';
		let statusClass = '';
		let body = null;

		switch (pin.state) {
			case PIN_STATE.UNCLAIMED:
				body = (
					<div className="milestone-claim-actions">
						<Button
							type="primary"
							shape="round"
							size="large"
							block
							loading={claimingPin}
							disabled={decliningPin}
							onClick={handleClaimPin}
						>
							Claim Pin
						</Button>
						<Popconfirm
							title="Are you sure? This action cannot be undone."
							onConfirm={handleDeclinePin}
							okText="Yes"
							cancelText="No"
							disabled={claimingPin || decliningPin}
						>
							<Button
								type="link"
								size="small"
								loading={decliningPin}
								disabled={claimingPin}
							>
								Decline pin
							</Button>
						</Popconfirm>
					</div>
				);
				break;
			case PIN_STATE.STARTED:
				status = 'Started';
				statusClass = 'is-started';
				body = (
					<div className="milestone-claim-actions">
						<Button
							shape="round"
							size="large"
							block
							onClick={() => openLink(pin.link)}
						>
							Finish Claiming Pin
						</Button>
					</div>
				);
				break;
			case PIN_STATE.CLAIMED:
				status = 'Claimed';
				statusClass = 'is-done';
				body = (
					<span className="milestone-claim-note">
						We'll email you when it ships.
					</span>
				);
				break;
			case PIN_STATE.DECLINED:
				status = 'Declined';
				statusClass = 'is-muted';
				body = (
					<span className="milestone-claim-note">
						You declined this reward.
					</span>
				);
				break;
			default:
				status = 'Expired';
				statusClass = 'is-muted';
				body = (
					<span className="milestone-claim-note">
						The claim window has passed.
					</span>
				);
		}

		return (
			<div
				className={`milestone-claim-card ${pinResolved ? 'is-resolved' : ''}`}
			>
				<div className="milestone-claim-image is-round">
					{pin.image ? (
						<img src={pin.image} alt={pin.reward?.name || 'Service pin'} />
					) : null}
					{pin.state === PIN_STATE.CLAIMED && <ClaimedMark />}
				</div>
				<span className={`milestone-claim-status ${statusClass}`}>
					{status}
				</span>
				<span className="milestone-claim-name">Your service pin</span>
				{body}
			</div>
		);
	};

	const renderGiftCard = () => {
		let status = giftClaimBy ? `Reward 2 · claim by ${giftClaimBy}` : 'Reward 2';
		let statusClass = '';
		let body = null;
		const isHighlighted = pinResolved && isGiftOutstanding(claim);

		switch (gift.state) {
			case GIFT_STATE.UNCLAIMED:
			case GIFT_STATE.STARTED:
				if (pinResolved) {
					status = giftClaimBy
						? `Still to claim · by ${giftClaimBy}`
						: 'Still to claim';
					statusClass = 'is-highlight';
				}
				body = (
					<div className="milestone-claim-actions">
						<Button
							type="primary"
							shape="round"
							size="large"
							block
							loading={claimingGift}
							onClick={handleClaimGift}
						>
							{gift.state === GIFT_STATE.STARTED
								? 'Finish Claiming Gift'
								: 'Claim Gift'}
						</Button>
					</div>
				);
				break;
			case GIFT_STATE.CLAIMED:
				status = 'Claimed';
				statusClass = 'is-done';
				body = (
					<span className="milestone-claim-note">Enjoy your gift!</span>
				);
				break;
			case GIFT_STATE.EXPIRED:
				status = 'Expired';
				statusClass = 'is-muted';
				body = (
					<span className="milestone-claim-note">
						The claim window has passed.
					</span>
				);
				break;
			default:
				body = gift.link ? (
					<div className="milestone-claim-actions">
						<Button
							shape="round"
							size="large"
							block
							onClick={() => openLink(gift.link)}
						>
							Open in Snappy
						</Button>
					</div>
				) : null;
		}

		return (
			<div
				className={`milestone-claim-card ${giftResolved ? 'is-resolved' : ''} ${
					isHighlighted ? 'is-highlighted' : ''
				}`}
			>
				<div className="milestone-claim-image">
					<img src={GiftImage} alt="Anniversary gift" />
					{gift.state === GIFT_STATE.CLAIMED && <ClaimedMark />}
				</div>
				<span className={`milestone-claim-status ${statusClass}`}>
					{status}
				</span>
				<span className="milestone-claim-name">Your anniversary gift</span>
				{body}
			</div>
		);
	};

	const renderMessage = () => {
		if (isFirstVisit) {
			const modalMessage = recognitionSettings.milestoneClaimModalMessage;
			return modalMessage ? (
				<div
					className="milestone-claim-message"
					dangerouslySetInnerHTML={{
						__html: dompurify.sanitize(modalMessage),
					}}
				/>
			) : null;
		}

		const yearsText = claim.years ? `${claim.years}-year ` : '';
		if (pinResolved && isGiftOutstanding(claim)) {
			return (
				<p className="milestone-claim-message">
					{pin.state === PIN_STATE.CLAIMED ? 'Your pin is on its way. ' : ''}
					Your {yearsText}anniversary also comes with a gift, and it's still
					waiting for you.
				</p>
			);
		}
		if (giftResolved && isPinOutstanding(claim)) {
			return (
				<p className="milestone-claim-message">
					Your {yearsText}anniversary also comes with a service pin, and it's
					still waiting for you.
				</p>
			);
		}
		return null;
	};

	const renderFootnote = () => {
		if (!needsAttention(claim)) return null;
		if (isFirstVisit) {
			return (
				<p className="milestone-claim-footnote">
					Each reward is claimed separately. Both stay under{' '}
					<strong>My Recognitions &amp; Rewards</strong> until you claim them.
				</p>
			);
		}
		return (
			<p className="milestone-claim-footnote">
				We'll remind you here each time you sign in until your{' '}
				{isGiftOutstanding(claim) ? 'gift' : 'pin'} is claimed.
			</p>
		);
	};

	return (
		<Modal
			open={open}
			footer={null}
			onCancel={onClose}
			destroyOnClose
			centered
			width={600}
			className="combined-milestone-claim-modal"
		>
			{isExploding && (
				<div className="milestone-claim-confetti">
					<ConfettiExplosion {...confettiConfig} />
				</div>
			)}
			<div className="milestone-claim-content">
				<h2 className="milestone-claim-title">{title}</h2>
				<div className="milestone-claim-chip">
					{yearsLabel}
					{resolvedCount === 0
						? 'You have 2 rewards to claim'
						: `${resolvedCount} of 2 rewards claimed`}
				</div>
				<div className="milestone-claim-rewards">
					{renderPinCard()}
					<div className="milestone-claim-plus" aria-hidden="true">
						+
					</div>
					{renderGiftCard()}
				</div>
				{renderMessage()}
				{renderFootnote()}
				<Button shape="round" size="large" onClick={onClose}>
					{needsAttention(claim) ? "I'll claim later" : 'Close'}
				</Button>
			</div>
		</Modal>
	);
};

export default CombinedMilestoneClaimModal;
